import { HttpClient, HttpErrorResponse } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Observable, catchError, finalize, map, of, shareReplay, tap, throwError } from "rxjs";
import { environment } from "src/environments/environment";

interface JBagotAccessResponse {
  data?: {
    requestJBagotAccess?: {
      accessToken?: string;
      role?: string;
    };
  };
  errors?: Array<{ message?: string }>;
}

@Injectable({
    providedIn: 'root'
})
export class AuthService {
  private accessToken: string | null = null;
  private accessTokenRequest$: Observable<string> | null = null;
  private rateLimitedUntil = 0;

  constructor(private http: HttpClient) {}

  getAccessToken(): Observable<string> {
    return this.accessToken ? of(this.accessToken) : this.requestAccessToken();
  }

  // An expired request can arrive after another request already renewed the token.
  refreshAccessToken(expiredToken?: string): Observable<string> {
    if (this.accessToken && expiredToken && this.accessToken !== expiredToken) {
      return of(this.accessToken);
    }

    this.accessToken = null;
    return this.requestAccessToken();
  }

  private requestAccessToken(): Observable<string> {
    if (this.accessTokenRequest$) {
      return this.accessTokenRequest$;
    }
    if (Date.now() < this.rateLimitedUntil) {
      return throwError(
        () => new Error('JBagot access token requests are temporarily rate limited')
      );
    }

    const request = this.http
      .post<JBagotAccessResponse>(`${environment.graphql_apiUrl}/graphql`, {
        operationName: 'RequestJBagotAccess',
        query:
          'mutation RequestJBagotAccess { requestJBagotAccess { accessToken role } }',
      })
      .pipe(
        map((response) => {
          const token = response.data?.requestJBagotAccess?.accessToken;
          if (!token || response.errors?.length) {
            throw new Error(
              response.errors?.map((error) => error.message).join(', ') ||
                'JBagot access token was not returned'
            );
          }
          return token;
        }),
        tap((token) => (this.accessToken = token)),
        catchError((error) => {
          if (error instanceof HttpErrorResponse && error.status === 429) {
            const retryAfter = Number(error.headers.get('Retry-After'));
            this.rateLimitedUntil =
              Date.now() + (Number.isFinite(retryAfter) ? retryAfter * 1000 : 300000);
          }
          return throwError(() => error);
        }),
        finalize(() => (this.accessTokenRequest$ = null)),
        shareReplay({ bufferSize: 1, refCount: false })
      );

    this.accessTokenRequest$ = request;
    return request;
  }
}
